import {
  type EdgeId,
  type NodeSn,
  isNonNullable,
  isPathLookupEdgeData,
  isPathLookupNodeData,
} from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import { propOf } from '@ipfabric/utils/fp/prop';
import type {
  PacketDetail,
  PathLookupPathInspectorSelectedPacketSource,
} from 'components/DeviceView/deviceViewTypes';
import { highlightEdges } from 'components/Graph/commands/pathLookup/highlightEdges';
import { unhighlightEdges } from 'components/Graph/commands/pathLookup/unhighlightEdges';
import { EDGE_HIGHLIGHT_COLOR } from 'components/Graph/constants';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import type { GraphData } from 'components/Graph/model/GraphData';
import { pipe } from 'fp-ts/lib/pipeable';
import { addTrafficScoreToPacketDetail } from './helpers/addTrafficScoreToPacketDetail';
import { autoselectPacketDetail } from './helpers/autoselectPacketDetail';
import { filterOutNonEdgePackets } from './helpers/filterOutNonEdgePackets';
import { findNodeIdByNodeSn } from './helpers/findNodeIdByNodeSn';
import { getAutoselectedPacketSource } from './helpers/getAutoselectedPacketSource';
import { getFollowingPacketIds } from './helpers/getFollowingPacketIds';
import { getGeneratedPacketDetail } from './helpers/getGeneratedPacketDetail';
import { getPacketEdgeDetail } from './helpers/getPacketEdgeDetail';
import { hasOneRemainingPacketToSelect } from './helpers/hasOneRemainingPacketToSelect';

export interface SelectPathInspectorForwardedPacketCommand {
  nodeSn: NodeSn;
  edgeId: EdgeId;
  packetSource: PathLookupPathInspectorSelectedPacketSource;
}

const getPacketDetail = (
  graphData: GraphData,
  edgeId: EdgeId,
  nodeSn: NodeSn,
  packetSource: PathLookupPathInspectorSelectedPacketSource,
): PacketDetail | null => {
  if (packetSource === 'edge') {
    const foundEdge = graphData.edges[edgeId];
    if (foundEdge === undefined || !isPathLookupEdgeData(foundEdge)) {
      return null;
    }

    return getPacketEdgeDetail(graphData, edgeId);
  }
  if (packetSource === 'generatedPackets') {
    return pipe(
      findNodeIdByNodeSn(graphData, nodeSn),
      nullable.chain(propOf(graphData.nodes)),
      nullable.chainPredicate(isPathLookupNodeData),
      (nodeData) => nodeData?.generatedPackets?.[edgeId],
      nullable.chain(partialApply(getGeneratedPacketDetail, edgeId, ___)),
    );
  }
  return null;
};

const nodeHasValidFollowingSuccessorPacketId = (
  followingPacketIds: EdgeId[],
  successorPacketId?: EdgeId | null,
) =>
  isNonNullable(successorPacketId) &&
  followingPacketIds.includes(successorPacketId);

export const selectPathInspectorForwardedPacket: GraphCommandFactory<
  SelectPathInspectorForwardedPacketCommand
> = (cmdContext, dispatch) => (command) => {
  const { graph } = cmdContext.getState();
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  cmdContext.updateState((draft) => {
    draft.pathlookup.pathInspector.hoveredEdge = null;
    const activeNodeId = findNodeIdByNodeSn(draft.graph, command.nodeSn);
    if (activeNodeId === null || command.nodeSn === null) {
      return;
    }
    // Fixed noUncheckedIndexedAccess NIM-19408
    const nodeSelectedPackets =
      draft.pathlookup.pathInspector.selectedPackets[command.nodeSn]!;

    const detail = getPacketDetail(
      graph,
      command.edgeId,
      command.nodeSn,
      command.packetSource,
    );
    if (detail === null) {
      return;
    }

    // Close edge tooltip
    draft.tooltip = null;

    // Set selected edge
    const { highlightedEdges } = draft.pathlookup.pathInspector;
    if (nodeSelectedPackets.selectedIncomingPacket === null) {
      addTrafficScoreToPacketDetail(nodeSelectedPackets, draft.graph, detail);
      nodeSelectedPackets.selectedIncomingPacketSource = command.packetSource;
    } else if (nodeSelectedPackets.selectedOutgoingPacket === null) {
      nodeSelectedPackets.selectedOutgoingPacket = detail;
      nodeSelectedPackets.selectedOutgoingPacketSource = command.packetSource;
    } else {
      return;
    }

    // If user has selected edge from incoming edges
    if (nodeSelectedPackets.selectedOutgoingPacket === null) {
      dispatch(unhighlightEdges, {
        // Fixed noUncheckedIndexedAccess NIM-19408
        edges: highlightedEdges[command.nodeSn]!.incomingEdges,
      });
      // Fixed noUncheckedIndexedAccess NIM-19408
      highlightedEdges[command.nodeSn]!.incomingEdges = [];

      // Get following edges from graph edge and highlight them
      const followingPacketIds = getFollowingPacketIds(
        command.edgeId,
        draft.graph.edges[command.edgeId],
        draft.graph.nodes[activeNodeId],
      );
      if (
        followingPacketIds === null ||
        highlightedEdges[command.nodeSn] === undefined
      ) {
        return;
      }
      // Autoselect following edge if only 1 edge or 1 terminated packet is available
      if (
        nodeSelectedPackets.selectedIncomingPacket !== null &&
        (nodeHasValidFollowingSuccessorPacketId(
          followingPacketIds,
          nodeSelectedPackets.successorPacketId,
        ) ||
          hasOneRemainingPacketToSelect(
            draft.graph,
            command.edgeId,
            activeNodeId,
          ))
      ) {
        const packetDetail = autoselectPacketDetail(
          draft.graph,
          nodeSelectedPackets.selectedIncomingPacket.id,
          activeNodeId,
          nodeHasValidFollowingSuccessorPacketId(
            followingPacketIds,
            nodeSelectedPackets.successorPacketId,
          )
            ? nodeSelectedPackets.successorPacketId
            : undefined,
        );
        if (packetDetail !== null) {
          nodeSelectedPackets.selectedOutgoingPacket = packetDetail;
          nodeSelectedPackets.selectedOutgoingPacketSource =
            getAutoselectedPacketSource(
              draft.graph,
              activeNodeId,
              packetDetail,
            );
        }
      } else {
        // Highlight all following edges
        const followingEdgeIds = filterOutNonEdgePackets(draft.graph)(
          followingPacketIds,
        );
        if (followingEdgeIds !== null) {
          // Fixed noUncheckedIndexedAccess NIM-19408
          highlightedEdges[command.nodeSn]!.outgoingEdges = followingEdgeIds;
          dispatch(highlightEdges, {
            edges: followingEdgeIds,
            color: EDGE_HIGHLIGHT_COLOR,
          });
        }
      }
    } else {
      // User has selected second edge, unhighlight outgoing edges
      dispatch(unhighlightEdges, {
        // Fixed noUncheckedIndexedAccess NIM-19408
        edges: highlightedEdges[command.nodeSn]!.outgoingEdges,
      });
      // Fixed noUncheckedIndexedAccess NIM-19408
      highlightedEdges[command.nodeSn]!.outgoingEdges = [];
    }
  });
};
